import {useEffect, useMemo} from 'react';

import {safeLocalStorage} from '../../../../../util/localStorage';
import {type Sort} from '../../../../../util/queryTypes';
import {
  type SemanticLegendSettingsWithCategoricalConfig,
  YAxisValues,
} from '../../types';
import {DEFAULT_SEMANTIC_LEGEND_SETTINGS} from '../SemanticLegendDefaults';
import {normalizeNumber} from './numberNormalization';
import {useSemanticLegendRuntimeData} from './useSemanticLegendRuntimeData';
import {useSemanticLegendSettingsWithCategoricalConfig} from './useSemanticLegendSettingsWithCategoricalConfig';
export const useSemanticLegendSettingsQueryPropsWithCategoricalConfig = ({
  createSemanticLegendSettings,
  entityName,
  filters,
  order,
  projectName,
  viewID,
  viewName,
  configKeys,
  localKey,
  localYAxisValue,
}: {
  createSemanticLegendSettings: (
    settings: Partial<SemanticLegendSettingsWithCategoricalConfig>
  ) => void;
  entityName: string;
  filters: string;
  order: Sort;
  projectName: string;

  viewID: string;
  viewName: string;
  configKeys?: string[];

  // Allow overriding the current key with the local key in case of viewing
  // someone else's workspace
  localKey?: string;
  localYAxisValue?: YAxisValues;
}) => {
  const {
    constructedSemanticLegendData,
    currentKey,
    semanticLegendSettingsError,
    semanticLegendSettingsLoading,
  } = useSemanticLegendSettingsWithCategoricalConfig(
    viewID,
    configKeys,
    localKey,
    localYAxisValue
  );

  const isConfigKey = (configKeys ?? []).includes(currentKey ?? '');
  const runtimeData = useSemanticLegendRuntimeData({
    currentKey,
    enabled: !semanticLegendSettingsLoading,
    entityName,
    filters,
    keySource: isConfigKey ? 'config' : 'metric',
    order,
    projectName,
    yAxisValue: localYAxisValue ?? constructedSemanticLegendData.yAxisValue,
  });

  const combinedError = useMemo(
    () => semanticLegendSettingsError || runtimeData.error,
    [semanticLegendSettingsError, runtimeData.error]
  );

  const combinedLoading = useMemo(
    () => semanticLegendSettingsLoading || runtimeData.loading,
    [semanticLegendSettingsLoading, runtimeData.loading]
  );

  // Read any saved semantic legend from local storage once per view
  const savedSemanticLegendFromStorage = useMemo(() => {
    const stored = safeLocalStorage.getItem(
      `semanticLegendSettings-${viewName}`
    );
    if (!stored) {
      return undefined;
    }
    try {
      const parsed = JSON.parse(stored);
      return {
        ...parsed,
        viewID: viewID ?? '',
      } as Partial<SemanticLegendSettingsWithCategoricalConfig>;
    } catch (error) {
      // If parsing fails, remove the corrupted data and return undefined
      console.warn('Corrupted semantic legend data in localStorage:', error);
      safeLocalStorage.removeItem(`semanticLegendSettings-${viewName}`);
      return DEFAULT_SEMANTIC_LEGEND_SETTINGS;
    }
  }, [viewName, viewID]);

  // If we have a saved spec and we have a real viewID and not loading, create it and clear storage
  useEffect(() => {
    if (
      savedSemanticLegendFromStorage &&
      viewID &&
      !semanticLegendSettingsLoading
    ) {
      createSemanticLegendSettings(savedSemanticLegendFromStorage);
      safeLocalStorage.removeItem(`semanticLegendSettings-${viewName}`);
    }
  }, [
    savedSemanticLegendFromStorage,
    viewID,
    semanticLegendSettingsLoading,
    createSemanticLegendSettings,
    viewName,
  ]);

  // Retrieve the semantic legend settings
  const {
    data: semanticLegendSettingsData,
    specPreviouslyCreated,
  }: {
    data: SemanticLegendSettingsWithCategoricalConfig;
    specPreviouslyCreated: boolean;
  } = useMemo(() => {
    const base = {
      error: combinedError,
      loading: combinedLoading,
    };

    // If a valid semantic legend settings object exists, use it
    // we can use `currentMetric` to check this since we only have it when it destructures properly off a well-formed response
    if (currentKey) {
      const metricStatistics = {
        ...runtimeData.metricStatisticsAggregation,
        [YAxisValues.Config_Number]: runtimeData.configValuesNumberAggregation,
      };
      // Now that we have the metric statistics, we can set the true min for the first bucket
      // We previously set a placeholder min for the first bucket when we constructed the semantic legend data because we only store the bucket upper bounds
      const trueMin =
        metricStatistics?.[
          (constructedSemanticLegendData?.yAxisValue as Exclude<
            YAxisValues,
            YAxisValues.Config_Categories
          >) ?? YAxisValues.Min
        ]?.min ?? undefined;
      if (
        constructedSemanticLegendData?.buckets?.[0] &&
        trueMin !== undefined
      ) {
        // oxlint-disable-next-line react/immutability
        constructedSemanticLegendData.buckets[0].bucketMin =
          normalizeNumber(trueMin);
      }
      return {
        ...base,
        data: {
          metricStatistics,
          metricStatisticsForRun: runtimeData.metricStatisticsForRun,
          configValuesForRun: runtimeData.configValuesForRun,
          configValuesForRunCategories:
            runtimeData.configValuesForRunCategories,
          yAxisValue: localYAxisValue,
          ...constructedSemanticLegendData,
          viewID: viewID ?? '',
          allCategories: runtimeData.configValuesCategories,
        },
        specPreviouslyCreated: true,
      };
    }
    // if no semantic legend settings object exists, check local storage for a saved semantic legend settings object
    if (savedSemanticLegendFromStorage) {
      return {
        ...base,
        data: savedSemanticLegendFromStorage,
        specPreviouslyCreated: true,
      };
    }

    // default to the default semantic legend settings
    return {
      ...base,
      data: {
        ...DEFAULT_SEMANTIC_LEGEND_SETTINGS,
        viewID: viewID ?? '',
        allCategories: runtimeData.configValuesCategories,
      },
      specPreviouslyCreated: false,
    };
  }, [
    combinedError,
    combinedLoading,
    currentKey,
    savedSemanticLegendFromStorage,
    viewID,
    runtimeData.configValuesCategories,
    runtimeData.metricStatisticsAggregation,
    runtimeData.configValuesNumberAggregation,
    runtimeData.metricStatisticsForRun,
    runtimeData.configValuesForRun,
    runtimeData.configValuesForRunCategories,
    constructedSemanticLegendData,
    localYAxisValue,
  ]);

  return useMemo(
    () => ({
      semanticLegendSettingsData,
      semanticLegendSettingsLoading,
      semanticLegendSettingsError,
      specPreviouslyCreated,
      configValuesForRunCategories: runtimeData.configValuesForRunCategories,
    }),
    [
      semanticLegendSettingsData,
      semanticLegendSettingsLoading,
      semanticLegendSettingsError,
      specPreviouslyCreated,
      runtimeData.configValuesForRunCategories,
    ]
  );
};
